import { FC, useEffect, useState } from 'react';

import { useApplication } from '@eneco/common/application';
import RichText from '@eneco/components/RichText/RichText';
import {
  putEnecoBeXapiSiteApiV1OrdersByOrderIdDeliveryAndCorrespondenceAddress,
  UpdateDeliveryAndCorrespondenceAddressRequest,
} from '@eneco/dc-be/client';
import { unwrapValidationError } from '@eneco/dc-be/utils/unwrapError';
import {
  Bucket,
  Button,
  Heading,
  InputSelect,
  InputText,
  NotificationBox,
  RadioButton,
  RadioGroup,
  Stack,
  Stretch,
} from '@eneco/sparky';
import { HomeIcon } from '@eneco/sparky/icons';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementRendering } from '@sitecore/types/OrderManagement';
import { Controller, useFormContext } from 'react-hook-form';

import DataErrorNotification from '../../../components/DataErrorNotification';
import usePostalCodeBrusselsEligibilityCheck from '../../../hooks/usePostalCodeBrusselsEligibilityCheck';
import AddressAutocomplete from '../../../utils/addresses/AddressAutocomplete';
import { getCountryOptions } from '../../../utils/countries';
import { useOrderId } from '../hooks/useOrderId';
import { OrderManagementFormSchema, PROFESSIONAL_USER_OPTION_YES } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const DeliveryAddressStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementRendering>();
  const { language } = useApplication();
  const { trackEventBE } = useTracking();

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [showPostalCodeNotEligibleNotification, setShowPostalCodeNotEligibleNotification] = useState(false);
  const [error, setError] = useState<NotificationBoxProps | null>(null);
  const [hasUpdateError, setHasUpdateError] = useState(false);

  const orderId = useOrderId();

  // Get field references from delivery address step
  const addressStepFields = fields.addressStep;

  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    control,
    watch,
    trigger,
  } = useFormContext<OrderManagementFormSchema>();
  const postalCode = watch('deliveryAddressStep.postalCode');
  const country = watch('deliveryAddressStep.correspondenceAddress.country');
  const professionalUser = watch('personalDetailsStep.professionalUser');
  const customerType = professionalUser === PROFESSIONAL_USER_OPTION_YES ? 'SOHO' : 'Residential';

  const {
    isLoadingPostalCodeEligible,
    isPostalCodeEligible,
    shouldShowError: shouldShowNotification,
  } = usePostalCodeBrusselsEligibilityCheck(postalCode, customerType);

  useEffect(() => {
    if (postalCode?.length === 0) {
      setShowPostalCodeNotEligibleNotification(false);
      return;
    }
    setShowPostalCodeNotEligibleNotification(shouldShowNotification);
  }, [postalCode, shouldShowNotification]);

  const handleOnClickNext = async () => {
    if (!orderId) {
      setError({
        isAlert: true,
        variant: 'error',
        title: 'Error',
        text: 'Order ID is missing',
      });
      return;
    }

    setIsLoadingSubmit(true);

    if (isPostalCodeEligible) {
      setShowPostalCodeNotEligibleNotification(false);
    } else {
      setShowPostalCodeNotEligibleNotification(true);
      setIsLoadingSubmit(false);
      return;
    }

    const isValidDeliveryAddress = await trigger('deliveryAddressStep');
    const isValidCorrespondenceAddress = await trigger('deliveryAddressStep.correspondenceAddress');

    if (!isValidDeliveryAddress || !isValidCorrespondenceAddress) {
      setIsLoadingSubmit(false);
      return;
    }
    const values = getValues('deliveryAddressStep');

    // if body has empty delivery address values, log error
    if (!values.street || !values.houseNumber || !values.postalCode || !values.city) {
      console.log('Delivery address is missing required values', values);
      return;
    }

    const body: UpdateDeliveryAndCorrespondenceAddressRequest = {
      deliveryAddress: {
        street: values.street,
        houseNumber: values.houseNumber?.toString(),
        bus: values.busNumber || '',
        postalCode: values.postalCode,
        municipality: values.city,
      },
    };

    // Only include correspondence address if user selected "different address"
    if (values.correspondenceAddress && !values.correspondenceAddress?.isCorrespondenceAddressSameAsDeliveryAddress) {
      body.correspondenceAddress = {
        country: values.correspondenceAddress.country || '',
        postalCode: values.correspondenceAddress?.postalCode || '',
        municipality: values.correspondenceAddress?.city || '',
        street: values.correspondenceAddress?.street || '',
        houseNumber: values.correspondenceAddress?.houseNumber?.toString() || '',
        bus: values.correspondenceAddress?.busNumber || '',
      };
    }

    try {
      await putEnecoBeXapiSiteApiV1OrdersByOrderIdDeliveryAndCorrespondenceAddress({
        path: {
          orderId,
        },
        body,
      }).then(result => {
        const { response, error: resultError } = result;
        unwrapValidationError(resultError);

        if (response.ok) {
          setIsLoadingSubmit(false);
          trackEventBE('be_om_correspondence_addr', {
            status: 'success',
            data: getTrackableFormValues(getValues()),
          });
          nextMainStep();
        } else {
          trackEventBE('be_om_correspondence_addr', {
            status: 'failed',
            message: 'Failed to update order with delivery and correspondence address',
            data: getTrackableFormValues(getValues()),
          });
          setIsLoadingSubmit(false);
          setHasUpdateError(true);
        }
      });
    } catch {
      trackEventBE('be_om_correspondence_addr', {
        status: 'failed',
        message: 'Failed to update order with delivery and correspondence address',
        data: getTrackableFormValues(getValues()),
      });
      setIsLoadingSubmit(false);
      setHasUpdateError(true);
    }
  };

  if (!orderId) {
    return (
      <Bucket title={addressStepFields.title.value}>
        <Bucket.Content>
          <NotificationBox
            isAlert
            variant="error"
            title={fields.overviewStep.errorNotification.value.title || 'Error [not in sitecore]'}
            text={fields.overviewStep.errorNotification.value.content || '[not in sitecore]: Order ID is missing'}
          />
        </Bucket.Content>
      </Bucket>
    );
  }

  const isCorrespondenceAddressSameAsDeliveryAddress = watch(
    'deliveryAddressStep.correspondenceAddress.isCorrespondenceAddressSameAsDeliveryAddress',
  );

  return (
    <Stack gap="10">
      {hasUpdateError && <DataErrorNotification />}
      <Stack gap="2">
        <Heading as="h2" size="M">
          {fields.addressStep.title.value}
        </Heading>
        <RichText html={fields.addressStep.subTitle.value} />
      </Stack>
      <Stack gap="10" alignX={'start'}>
        <Bucket
          title={
            <Stack alignY="center" gap="2" alignX={'start'} direction={'row'}>
              <HomeIcon />
              <span>
                {addressStepFields?.communicationAddressLabel?.value ?? '[not in sitecore]: Delivery Address'}
              </span>
            </Stack>
          }>
          <Bucket.Content>
            <AddressAutocomplete
              cityFormField={{
                ...addressStepFields.cityFormField.value,
                defaultValue: getValues('deliveryAddressStep.city') ?? '',
                yupProps: register('deliveryAddressStep.city'),
              }}
              onZipCodeCityChange={value => {
                setValue('deliveryAddressStep.postalCode', value.zipCode as string, { shouldValidate: true });
                setValue('deliveryAddressStep.city', value.city as string, { shouldValidate: true });
              }}
              zipCodeFormField={{
                ...addressStepFields.zipCodeFormField.value,
                defaultValue: getValues('deliveryAddressStep.postalCode') ?? '',
                yupProps: register('deliveryAddressStep.postalCode'),
              }}
              zipCodeCityError={addressStepFields.zipCodeFormField.value.validationMessage}
              isApiAddressValidYupProps={register('deliveryAddressStep.isApiPostalCodeCityValid')}
              streetNameFormField={{
                ...addressStepFields.streetFormField.value,
                defaultValue: getValues('deliveryAddressStep.street') ?? '',
                yupProps: register('deliveryAddressStep.street'),
              }}
              onStreetNameChange={streetName => {
                setValue('deliveryAddressStep.street', streetName, { shouldValidate: true });
              }}
              streetNameNotFoundWarningTitle={addressStepFields.streetNameNotFoundNotification?.value?.title}
              streetNameNotFoundWarningContent={addressStepFields.streetNameNotFoundNotification?.value?.content}
              shouldValidateAddressInApi={true}
            />

            <Stack direction="row" gap="6">
              <Stretch width>
                <div>
                  <InputText
                    label={addressStepFields.houseNumberFormField.value.label}
                    error={errors.deliveryAddressStep?.houseNumber?.message}
                    {...register('deliveryAddressStep.houseNumber')}
                  />
                </div>
              </Stretch>
              <Stretch width>
                <div>
                  <InputText
                    label={addressStepFields.busNumberFormField.value.label}
                    error={errors.deliveryAddressStep?.busNumber?.message}
                    {...register('deliveryAddressStep.busNumber')}
                  />
                </div>
              </Stretch>
            </Stack>
            {showPostalCodeNotEligibleNotification && (
              <NotificationBox
                title={addressStepFields?.postalCodeCheckNotification?.value?.title ?? '[not in sitecore]: Foutmelding'}
                text={
                  <RichText
                    html={
                      addressStepFields?.postalCodeCheckNotification?.value?.content ??
                      '[not in sitecore]: We leveren niet in Brussel'
                    }
                  />
                }
                variant={addressStepFields?.postalCodeCheckNotification?.value?.variant ?? 'error'}
                isAlert={false}
              />
            )}
          </Bucket.Content>
        </Bucket>
        <Bucket title={addressStepFields.addressFormTitle.value}>
          <Bucket.Content>
            <Controller
              control={control}
              name="deliveryAddressStep.correspondenceAddress.isCorrespondenceAddressSameAsDeliveryAddress"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  name="deliveryAddressStep.correspondenceAddress.isCorrespondenceAddressSameAsDeliveryAddress"
                  aria-labelledby="isCorrespondenceAddressSameAsDeliveryAddress"
                  direction="column"
                  value={value === false ? 'no' : 'yes'}
                  onValueChange={newValue => onChange(newValue === 'yes')}>
                  <RadioButton value="yes">{addressStepFields.sameAddressLabel.value}</RadioButton>
                  <RadioButton value="no">{addressStepFields.differentAddressLabel.value}</RadioButton>
                </RadioGroup>
              )}
            />
            {isCorrespondenceAddressSameAsDeliveryAddress === false && (
              <>
                <InputSelect
                  label={addressStepFields.countryFormField.value.label}
                  hint={<RichText html={addressStepFields.countryFormField.value.hint} />}
                  error={errors.deliveryAddressStep?.correspondenceAddress?.country?.message ?? ''}
                  placeholder=""
                  options={getCountryOptions(language)}
                  {...register('deliveryAddressStep.correspondenceAddress.country')}
                />
                <AddressAutocomplete
                  cityFormField={{
                    ...addressStepFields.cityFormField.value,
                    defaultValue: getValues('deliveryAddressStep.correspondenceAddress.city') ?? '',
                    yupProps: register('deliveryAddressStep.correspondenceAddress.city'),
                  }}
                  onZipCodeCityChange={value => {
                    setValue('deliveryAddressStep.correspondenceAddress.postalCode', value.zipCode as string, {
                      shouldValidate: true,
                    });
                    setValue('deliveryAddressStep.correspondenceAddress.city', value.city as string, {
                      shouldValidate: true,
                    });
                  }}
                  zipCodeFormField={{
                    ...addressStepFields.zipCodeFormField.value,
                    defaultValue: getValues('deliveryAddressStep.correspondenceAddress.postalCode') ?? '',
                    yupProps: register('deliveryAddressStep.correspondenceAddress.postalCode'),
                  }}
                  zipCodeCityError={addressStepFields.zipCodeFormField.value.validationMessage}
                  streetNameFormField={{
                    ...addressStepFields.streetFormField.value,
                    defaultValue: getValues('deliveryAddressStep.correspondenceAddress.street') ?? '',
                    yupProps: register('deliveryAddressStep.correspondenceAddress.street'),
                  }}
                  onStreetNameChange={streetName => {
                    setValue('deliveryAddressStep.correspondenceAddress.street', streetName, { shouldValidate: true });
                  }}
                  streetNameNotFoundWarningTitle={addressStepFields.streetNameNotFoundNotification?.value?.title}
                  streetNameNotFoundWarningContent={addressStepFields.streetNameNotFoundNotification?.value?.content}
                  isApiAddressValidYupProps={register(
                    'deliveryAddressStep.correspondenceAddress.isApiPostalCodeCityValid',
                  )}
                  shouldValidateAddressInApi={country === 'BE' || country === undefined}
                />

                <Stack direction="row" gap="6">
                  <Stretch width>
                    <div>
                      <InputText
                        label={addressStepFields.houseNumberFormField.value.label}
                        hint={<RichText html={addressStepFields.houseNumberFormField.value.hint} />}
                        error={errors.deliveryAddressStep?.correspondenceAddress?.houseNumber?.message ?? ''}
                        {...register('deliveryAddressStep.correspondenceAddress.houseNumber')}
                      />
                    </div>
                  </Stretch>
                  <Stretch width>
                    <div>
                      <InputText
                        label={addressStepFields.busNumberFormField.value.label}
                        hint={<RichText html={addressStepFields.busNumberFormField.value.hint} />}
                        error={errors.deliveryAddressStep?.correspondenceAddress?.busNumber?.message ?? ''}
                        {...register('deliveryAddressStep.correspondenceAddress.busNumber')}
                      />
                    </div>
                  </Stretch>
                </Stack>
              </>
            )}
          </Bucket.Content>
        </Bucket>

        {error && <NotificationBox {...error} />}

        <Button isLoading={isLoadingSubmit || isLoadingPostalCodeEligible} onClick={handleOnClickNext}>
          {fields.navigation.nextButtonLabel.value}
        </Button>
      </Stack>
    </Stack>
  );
};
